import React from 'react';
import { ptBR } from 'date-fns/locale';
import DateFnsUtils from '@date-io/date-fns';
import { Autocomplete } from '@material-ui/lab';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { createStyles, FormControl, FormControlLabel, FormLabel, Grid, Icon, IconButton, makeStyles,
    Radio, RadioGroup, TextField, Typography } from '@material-ui/core';

import { ClientModel, clientModelInitial } from '../../../Models/ClientModel';
import { KeywordInclusionContext } from '../KeywordInclusionContext';

const useStyles = makeStyles(() => createStyles({
    scheduleIcon: {
        padding: 0
    }
}));

export function KeywordInclusionFilterFields() {
    const classes = useStyles();
    
    const keywordInclusionContext = React.useContext(KeywordInclusionContext);

    const [initialDateFocused, setInitialDateFocused] = React.useState<boolean>(false);
    const [finalDateFocused, setFinalDateFocused] = React.useState<boolean>(false);
    
    const getClientField = () => {
        return (
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    id="clients"
                    value={keywordInclusionContext.selectedClient}
                    onChange={(_, newValue: ClientModel | null) => {
                        if (newValue)
                            keywordInclusionContext.setSelectedClient(newValue);
                        else
                            keywordInclusionContext.setSelectedClient(clientModelInitial);
                    }}
                    options={keywordInclusionContext.clientList}
                    getOptionLabel={(option: ClientModel) => option.name}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Cliente"
                            variant="outlined"
                            error={keywordInclusionContext.clientError &&
                                (!keywordInclusionContext.selectedClient || keywordInclusionContext.selectedClient.id <= 0)}
                            helperText={keywordInclusionContext.clientError &&
                                (!keywordInclusionContext.selectedClient || keywordInclusionContext.selectedClient.id <= 0)
                                ? 'Campo obrigatório'
                                : ''} />
                    } />
            </Grid>
        );
    };

    const getInitialDateField = () => {
        return (
            <Grid item xs={6} sm={3}>
                <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
                    <KeyboardDatePicker
                        id="search-date"
                        autoOk
                        disableFuture
                        allowKeyboardControl
                        variant="inline"
                        inputVariant="outlined"
                        label='Início'
                        value={keywordInclusionContext.selectedInitialDate}
                        open={initialDateFocused}
                        onBlur={() => setInitialDateFocused(false)}
                        onClose={() => setInitialDateFocused(false)}
                        onChange={(date) => keywordInclusionContext.setSelectedInitialDate(date)}
                        error={keywordInclusionContext.initialDateError && !keywordInclusionContext.selectedInitialDate}
                        helperText={keywordInclusionContext.initialDateError && !keywordInclusionContext.selectedInitialDate
                            ? 'Campo obrigatório'
                            : ''}
                        format="dd/MM/yyyy"
                        invalidDateMessage="Formato de data inválido"
                        minDateMessage="A data não deve exceder a data mínima"
                        maxDateMessage="A data não deve exceder a data máxima"
                        keyboardIcon={
                            <IconButton onClick={() => setInitialDateFocused(true)} className={classes.scheduleIcon}>
                                <Icon>date_range</Icon>
                            </IconButton>
                        } />
                </MuiPickersUtilsProvider>
            </Grid>
        );
    };

    const getFinalDateField = () => {
        return (
            <Grid item xs={6} sm={3}>
                <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
                    <KeyboardDatePicker
                        id="search-date"
                        autoOk
                        disableFuture
                        allowKeyboardControl
                        variant="inline"
                        inputVariant="outlined"
                        label='Fim'
                        value={keywordInclusionContext.selectedFinalDate}
                        minDate={keywordInclusionContext.selectedInitialDate}
                        open={finalDateFocused}
                        onBlur={() => setFinalDateFocused(false)}
                        onClose={() => setFinalDateFocused(false)}
                        onChange={(date) => keywordInclusionContext.setSelectedFinalDate(date)}
                        disabled={!keywordInclusionContext.selectedInitialDate}
                        error={keywordInclusionContext.finalDateError && !keywordInclusionContext.selectedFinalDate}
                        helperText={keywordInclusionContext.finalDateError && !keywordInclusionContext.selectedFinalDate
                            ? 'Campo obrigatório'
                            : ''}
                        format="dd/MM/yyyy"
                        invalidDateMessage="Formato de data inválido"
                        minDateMessage="A data não deve exceder a data mínima"
                        maxDateMessage="A data não deve exceder a data máxima"
                        keyboardIcon={
                            <IconButton onClick={() => setFinalDateFocused(true)} className={classes.scheduleIcon}>
                                <Icon>date_range</Icon>
                            </IconButton>
                        } />
                </MuiPickersUtilsProvider>
            </Grid>
        );
    };

    const getDateType = () => {
        return(
            <Grid item xs={6} sm={2}>
                <FormControl
                    component="fieldset"
                    size='small'
                    disabled={true}>
                    <FormLabel component="legend">
                        <Typography variant="subtitle2" color="textPrimary">Tipo de data</Typography>
                    </FormLabel>
                    <RadioGroup
                        row
                        aria-label="date-type"
                        name="search-date-type"
                        value={1}>
                        <FormControlLabel 
                            value={1}
                            control={ <Radio color='secondary' size='small' /> } 
                            label={
                                <Typography
                                    variant="body1"
                                    color="textPrimary">
                                    Publicação
                                </Typography>
                            } />
                    </RadioGroup>
                </FormControl>
            </Grid>
        );
    };

    return (
        <Grid container direction="row" spacing={3}>
            { getClientField() }
            { getInitialDateField() }
            { getFinalDateField() }
            { getDateType() }
        </Grid>
    );
}